import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, ArrowUp, ArrowDown, ToggleLeft, ToggleRight, X } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTER_TYPES = [
  { value: 'dropdown', label: 'Dropdown (list of options)' },
  { value: 'text', label: 'Text input' },
  { value: 'number', label: 'Number (exact)' },
  { value: 'range', label: 'Range (min / max)' },
];

const emptyForm = {
  filter_id: null,
  label: '',
  key: '',
  type: 'dropdown',
  options: '',
  category: '',
  subcategory: '',
  active: true,
};

export const SearchFiltersManagement = () => {
  const [filters, setFilters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [flt, cats] = await Promise.all([
        axios.get(`${API}/admin/search-filters`, { withCredentials: true }),
        axios.get(`${API}/categories`),
      ]);
      setFilters(flt.data);
      setCategories(cats.data);
    } catch (e) {
      toast.error('Failed to load search filters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (f) => {
    setForm({
      filter_id: f.filter_id,
      label: f.label,
      key: f.key,
      type: f.type,
      options: (f.options || []).join('\n'),
      category: f.category,
      subcategory: f.subcategory || '',
      active: !!f.active,
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.label.trim() || !form.key.trim() || !form.category.trim()) {
      toast.error('Label, key, and category are required');
      return;
    }
    if (form.type === 'dropdown') {
      const opts = form.options.split('\n').map(o => o.trim()).filter(Boolean);
      if (opts.length === 0) {
        toast.error('Dropdown filters require at least one option');
        return;
      }
    }
    setSaving(true);
    const payload = {
      label: form.label.trim(),
      key: form.key.trim(),
      type: form.type,
      options: form.type === 'dropdown'
        ? form.options.split('\n').map(o => o.trim()).filter(Boolean)
        : [],
      category: form.category,
      subcategory: form.subcategory || null,
      active: form.active,
    };
    try {
      if (form.filter_id) {
        await axios.put(`${API}/admin/search-filters/${form.filter_id}`, payload, { withCredentials: true });
        toast.success('Filter updated');
      } else {
        await axios.post(`${API}/admin/search-filters`, payload, { withCredentials: true });
        toast.success('Filter created');
      }
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (f) => {
    try {
      await axios.patch(`${API}/admin/search-filters/${f.filter_id}/toggle`, {}, { withCredentials: true });
      load();
    } catch (e) {
      toast.error('Toggle failed');
    }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete filter "${f.label}"?`)) return;
    try {
      await axios.delete(`${API}/admin/search-filters/${f.filter_id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Group filters by scope for reordering within a scope
  const groupKey = (f) => `${f.category}||${f.subcategory || ''}`;
  const reorder = async (f, dir) => {
    const siblings = filters.filter(x => groupKey(x) === groupKey(f))
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = siblings.findIndex(x => x.filter_id === f.filter_id);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= siblings.length) return;
    const reordered = [...siblings];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      await axios.post(`${API}/admin/search-filters/reorder`,
        { filter_ids: reordered.map(x => x.filter_id) },
        { withCredentials: true });
      load();
    } catch (e) {
      toast.error('Reorder failed');
    }
  };

  const selectedCat = categories.find(c => c.value === form.category);

  const catFilterValue = filterCategory;
  const visibleFilters = catFilterValue === 'all'
    ? filters
    : filters.filter(f => f.category === catFilterValue);

  // Group visible filters by category+subcategory for display
  const grouped = visibleFilters.reduce((acc, f) => {
    const k = `${f.category} / ${f.subcategory || '★ all subcategories'}`;
    (acc[k] = acc[k] || []).push(f);
    return acc;
  }, {});
  Object.keys(grouped).forEach(k => grouped[k].sort((a, b) => (a.order || 0) - (b.order || 0)));

  return (
    <div data-testid="search-filters-management">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold">Search Filters</h2>
          <p className="text-sm text-text_muted">Admin-controlled filters shown on the Browse page when users pick a category/subcategory. Separate from Custom Fields used in listing creation.</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-white" data-testid="create-search-filter-btn">
          <Plus size={16} className="mr-1.5" weight="bold" /> New Filter
        </Button>
      </div>

      <div className="mb-4 flex items-center gap-3">
        <Label className="text-sm">Filter by category:</Label>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-64" data-testid="filter-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label || c.value}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-text_muted">Loading…</p>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-8 text-center" data-testid="empty-search-filters">
          <p className="text-text_muted">No search filters yet. Click "New Filter" to add one.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([scope, list]) => (
            <div key={scope} className="bg-white border border-border rounded-lg p-4" data-testid={`filter-group-${scope}`}>
              <p className="text-sm font-semibold text-text_main mb-3">{scope}</p>
              <div className="space-y-2">
                {list.map((f, idx) => (
                  <div key={f.filter_id} className="flex items-center gap-3 border border-border rounded-md p-3" data-testid={`search-filter-row-${f.filter_id}`}>
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => reorder(f, -1)}
                        disabled={idx === 0}
                        className="text-text_muted hover:text-primary disabled:opacity-30"
                        data-testid={`move-up-${f.filter_id}`}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        onClick={() => reorder(f, 1)}
                        disabled={idx === list.length - 1}
                        className="text-text_muted hover:text-primary disabled:opacity-30"
                        data-testid={`move-down-${f.filter_id}`}
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{f.label}</span>
                        <Badge variant="outline" className="text-xs">{f.type}</Badge>
                        <code className="text-xs bg-surface px-1.5 py-0.5 rounded text-text_muted">key: {f.key}</code>
                      </div>
                      {f.type === 'dropdown' && f.options?.length > 0 && (
                        <p className="text-xs text-text_muted mt-1 truncate">
                          Options: {f.options.join(', ')}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => toggleActive(f)}
                      className={`p-1 ${f.active ? 'text-success' : 'text-text_muted'}`}
                      title={f.active ? 'Active — click to disable' : 'Inactive — click to enable'}
                      data-testid={`toggle-${f.filter_id}`}
                    >
                      {f.active ? <ToggleRight size={28} weight="fill" /> : <ToggleLeft size={28} />}
                    </button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(f)} data-testid={`edit-${f.filter_id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => del(f)} className="text-danger hover:bg-danger-soft" data-testid={`delete-${f.filter_id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" data-testid="search-filter-modal">
          <DialogHeader>
            <DialogTitle>{form.filter_id ? 'Edit Search Filter' : 'New Search Filter'}</DialogTitle>
            <DialogDescription>Shown on the Browse page only when users select the matching category / subcategory.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Label *</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="e.g. Brand, Transmission" data-testid="input-label" />
            </div>
            <div>
              <Label>Key * <span className="text-xs text-text_muted">(must match listing custom field key)</span></Label>
              <Input value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} placeholder="e.g. brand, fuel_type" data-testid="input-key" />
            </div>
            <div>
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="select-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FILTER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.type === 'dropdown' && (
              <div>
                <Label>Options * <span className="text-xs text-text_muted">(one per line)</span></Label>
                <textarea
                  value={form.options}
                  onChange={e => setForm({ ...form, options: e.target.value })}
                  rows={5}
                  className="w-full border border-border rounded px-3 py-2 text-sm font-mono"
                  placeholder="Toyota&#10;Ford&#10;Renault"
                  data-testid="input-options"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category *</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v, subcategory: '' })}>
                  <SelectTrigger data-testid="select-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label || c.value}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subcategory <span className="text-xs text-text_muted">(optional)</span></Label>
                <Select value={form.subcategory || '_all'} onValueChange={v => setForm({ ...form, subcategory: v === '_all' ? '' : v })} disabled={!form.category}>
                  <SelectTrigger data-testid="select-subcategory"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">★ All subcategories</SelectItem>
                    {(selectedCat?.subcategories || []).map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label || s.value}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sf-active"
                checked={form.active}
                onChange={e => setForm({ ...form, active: e.target.checked })}
                data-testid="input-active"
              />
              <Label htmlFor="sf-active" className="cursor-pointer">Active (visible on Browse page)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} data-testid="cancel-btn">
              <X size={14} className="mr-1" /> Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-white" data-testid="save-btn">
              {saving ? 'Saving…' : form.filter_id ? 'Update Filter' : 'Create Filter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
